import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertUprightBody, UpdateUprightBody, Upright } from "./types/uprights";

export const useUprightsApi = () => {
  const api = useApi();

  const getAdminUpright = useCallback(
    (uprightId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/uprights/${uprightId}`;
      return useSWRMutation<Upright, CustomError, string>(url, (url) => api.get<Upright>({ url, signal }));
    },
    [api],
  );

  const useAdminUprights = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/uprights`, query);
      return useSWRImmutable<Paginated<Upright>, CustomError, string>(url, (url) =>
        api.get<Paginated<Upright>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminUprights = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/uprights`, query);
      return api.get<Paginated<Upright>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminUpright = useCallback(
    async (body: InsertUprightBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/uprights`;
      const id = await api.post<{ id: string }, InsertUprightBody>(url, body);
      mutateCache([url, "/admin/settings/border/upright_caps"]);
      return id;
    },
    [api],
  );

  const updateAdminUpright = useCallback(
    async (uprightId: string, body: UpdateUprightBody): Promise<void> => {
      const url = `/admin/settings/border/uprights/${uprightId}`;
      await api.put<UpdateUprightBody>(url, body);
      mutateCache(["/admin/settings/border/uprights", "/admin/settings/border/upright_caps"]);
    },
    [api],
  );

  const updateAdminUprightVisibility = useCallback(
    async (uprightId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/uprights/${uprightId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/uprights", "/admin/settings/border/upright_caps"]));
    },
    [api],
  );

  const updateAdminUprightOrder = useCallback(
    async (uprightId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/uprights/${uprightId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/uprights", "/admin/settings/border/upright_caps"]));
    },
    [api],
  );

  const deleteAdminUpright = useCallback(
    async (upright: Upright): Promise<void> => {
      const url = `/admin/settings/border/uprights/${upright.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/uprights`, "/admin/settings/border/upright_caps"]);
    },
    [api],
  );

  return {
    getAdminUpright,
    insertAdminUpright,
    updateAdminUpright,
    updateAdminUprightVisibility,
    updateAdminUprightOrder,
    deleteAdminUpright,
    useAdminUprights,
    getAdminUprights,
  };
};
